import React, { useCallback, useRef, useState } from 'react';
import styled from 'styled-components';
import Typography from '@mui/material/Typography';
import { DistrictSelectInput } from '../../components';
import { District, DistrictSource } from '../../app/feathers/districts/District';
import { StateConfiguration } from '../../app/feathers/state-configurations/StateConfiguration';
import { stateCodeForName } from '../../app/util/clean-states';
import { humanizeDistrictType } from '../../app/util/humanize-district-type';
import { IconButton, Skeleton, CircularProgress } from '@mui/material';
import { AccountTree, Add, Cancel, CheckCircle, OpenInNew, Undo } from '@mui/icons-material';
import { CreateDistrictModal } from './CreateDistrictModal';
import { CreateDistrictSuggestion } from './create-district-modal-helpers';

const getSuggestionText = (value: unknown): string | undefined => {
  if (typeof value === 'string') return value;
  if (!value || typeof value !== 'object') return undefined;

  const districtValue = value as Partial<District>;
  return [
    districtValue.longName,
    districtValue.name,
    districtValue.shortName,
    districtValue._id,
  ].find(candidate => typeof candidate === 'string' && candidate.trim() !== '');
};

const normalizeSuggestion = (
  suggestion?: CreateDistrictSuggestion,
): CreateDistrictSuggestion | undefined => {
  if (!suggestion) return undefined;

  const runtimeSuggestion = suggestion as CreateDistrictSuggestion & {
    parentId?: unknown;
    parentDistrict?: unknown;
    name?: unknown;
    longName?: unknown;
    letter?: unknown;
  };

  return {
    ...suggestion,
    parentId: getSuggestionText(runtimeSuggestion.parentId),
    parentDistrict: getSuggestionText(runtimeSuggestion.parentDistrict),
    name: getSuggestionText(runtimeSuggestion.name),
    longName: getSuggestionText(runtimeSuggestion.longName),
    letter: getSuggestionText(runtimeSuggestion.letter),
  };
};

interface DistrictCellProps {
  district?: District | null;
  suggestCreationAs?: CreateDistrictSuggestion;
  onDistrictChange?: (newDistrict: District) => void;
  onDistrictClear?: () => void;
  state: string;
  stateConfiguration?: StateConfiguration | null;
  loading?: boolean;
  confidence?: number;
  onChangeConfidence?: (confidence: number) => void;
  limitTypes?: string[];
  disableEditing?: boolean;
  readOnly?: boolean;
  saving?: boolean;
  suggestedGeometry?: unknown;
  loadSuggestedGeometry?: () => Promise<unknown | undefined>;
  suggestedParentDistrict?: District;
  loadSuggestedParentDistrict?: () => Promise<District | undefined>;
  creationSources?: DistrictSource[];
  creationNotes?: string[];
  allowChildCreation?: boolean;
}

const DistrictCell: React.FC<DistrictCellProps> = ({ 
  district, 
  suggestCreationAs, 
  onDistrictChange, 
  onDistrictClear,
  state, 
  stateConfiguration,
  loading, 
  confidence, 
  onChangeConfidence,
  limitTypes,
  disableEditing,
  readOnly,
  saving,
  suggestedGeometry,
  loadSuggestedGeometry,
  suggestedParentDistrict,
  loadSuggestedParentDistrict,
  creationSources,
  creationNotes,
  allowChildCreation
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const loadSuggestedGeometryRef = useRef(loadSuggestedGeometry);
  loadSuggestedGeometryRef.current = loadSuggestedGeometry;
  const loadSuggestedParentDistrictRef = useRef(loadSuggestedParentDistrict);
  loadSuggestedParentDistrictRef.current = loadSuggestedParentDistrict;
  const suggestion = normalizeSuggestion(suggestCreationAs);
  const getSuggestedGeometry = useCallback(
    async () => loadSuggestedGeometryRef.current?.(),
    []
  );
  const getSuggestedParentDistrict = useCallback(
    async () => loadSuggestedParentDistrictRef.current?.(),
    []
  );

  const handleDistrictChange = (selectedOption: any) => {
    if (selectedOption && selectedOption.target?.value && onDistrictChange) {
      onDistrictChange(selectedOption.target.value);
    }
    setIsEditing(false);
  };

  const handleClick = () => {
    // If there's no district but there's a suggestCreationAs value, open modal
    if (!district && suggestion) {
      setIsModalOpen(true);
      return;
    }
    
    setIsEditing(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleCreateDistrict = (newDistrict: District) => {
    setIsModalOpen(false);
    onDistrictChange?.(newDistrict);
  };
  const districtTier = stateConfiguration?.typeConfigurations
    .find(configuration => configuration.type === district?.type)?.tierNumber;
  const childTypes = district
    ? stateConfiguration?.typeStructure?.[district.type] || []
    : [];
  const canCreateChild = Boolean(
    allowChildCreation
    && district
    && districtTier === 2
    && district.childrenDefined === false
  );
  const childCreationSuggestion: CreateDistrictSuggestion | undefined = canCreateChild
    ? {
      parentId: district?._id,
      parentDistrict: district?.name || district?.longName,
    }
    : undefined;
  const tier3ParentType = districtTier === 3
    ? Object.entries(stateConfiguration?.typeStructure || {}).find(
      ([parentType, childTypes]) => (
        childTypes.includes(district?.type || '')
        && stateConfiguration?.typeConfigurations.find(
          configuration => configuration.type === parentType
        )?.tierNumber === 2
      )
    )?.[0]
    : undefined;
  const detailDistrictId = districtTier === 3 ? district?.parentId : district?._id;
  const detailDistrictType = districtTier === 3 ? tier3ParentType : district?.type;
  const stateCode = (
    stateConfiguration?.stateCode
    || (district?.parent ? stateCodeForName(district.parent) : undefined)
  )?.toLowerCase();
  const stateMatchName = stateCode
    ? (stateCode === 'ga' ? stateCode : `${stateCode}-state`)
    : undefined;
  const districtDetailUrl = district && stateMatchName && detailDistrictType && detailDistrictId
    ? `/states/${stateMatchName}/${detailDistrictType}/${detailDistrictId}`
    : undefined;
  const districtDetailsButton = districtDetailUrl && (
    <IconButton
      component="a"
      href={districtDetailUrl}
      target="_blank"
      rel="noopener noreferrer"
      size="small"
      title={districtTier === 3 ? 'Open parent district details' : 'Open district details'}
      aria-label={
        districtTier === 3
          ? `Open parent district details for ${district?.longName || 'district'}`
          : `Open ${district?.longName || 'district'} details`
      }
    >
      <OpenInNew style={{ fontSize: '16px' }} />
    </IconButton>
  );

  if (loading) {
    return <Skeleton variant="text" width={100} height={24} />;
  }

  if(disableEditing) {
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="body2" style={{ fontWeight: '500' }}>-</Typography>
      </div>
    );
  }

  if (readOnly) {
    const readOnlyName = district?.longName
      || suggestion?.longName
      || suggestion?.name
      || '-';
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="body2" style={{ fontWeight: '500' }}>
          {readOnlyName}
        </Typography>
        {districtDetailsButton}
      </div>
    );
  }

  if (isEditing || (!district && !suggestion)) {
    return (
      <EditingContainer>
        <DistrictSelectInputAny
          state={state}
          value={null}
          onChange={handleDistrictChange}
          districtTypes={limitTypes}
          style={{ 
            minWidth: '120px',
          }}
        />
        {(district || suggestion) && (
          <IconButton
            aria-label={
              suggestion
                ? 'Return to create district suggestion'
                : 'Return to original district'
            }
            title={
              suggestion
                ? 'Return to create district suggestion'
                : 'Return to original district'
            }
            onClick={() => setIsEditing(false)}
            size="small"
          >
            <Undo fontSize="small" />
          </IconButton>
        )}
      </EditingContainer>
    );
  }

  // Show yellow tile for suggested district creation
  if (!district && suggestion) {
    const prettyType = humanizeDistrictType(
      suggestion.type || 'district',
      stateConfiguration?.typeConfigurations || null
    );
    const suggestionName = suggestion.name
      ?? suggestion.longName
      ?? suggestion.number
      ?? suggestion.letter
      ?? '';
    
    return (
      <>
        <div style={{ display: 'flex', alignItems: 'center' }}>  
          <SuggestedDistrictContainer onClick={handleClick}>
            <Typography variant="body2" style={{ color: '#8B4513' }}>
              Create <strong>{prettyType}</strong> district{' '}
              <strong>{suggestionName}</strong>
              {suggestion.parentDistrict && (
                <> in <strong>{suggestion.parentDistrict}</strong></>
              )}
            </Typography>
          </SuggestedDistrictContainer>
          <IconButton
            aria-label="Search for an existing district"
            title="Search for an existing district"
            onClick={() => setIsEditing(true)}
            style={{ marginLeft: '4px' }}
            size="small"
          >
            <Cancel fontSize="small" />
          </IconButton>
        </div>
        {isModalOpen && (
          <CreateDistrictModal
            suggestion={suggestion}
            stateConfiguration={stateConfiguration}
            suggestedGeometry={suggestedGeometry}
            loadSuggestedGeometry={getSuggestedGeometry}
            suggestedParentDistrict={suggestedParentDistrict}
            loadSuggestedParentDistrict={getSuggestedParentDistrict}
            creationSources={creationSources}
            creationNotes={creationNotes}
            onCreate={handleCreateDistrict}
            onClose={handleCloseModal}
          />
        )}
      </>
    );
  }

  const displayName = district?.longName || '-';

  // Show checkmark for low confidence labels when district is provided
  const showConfidenceCheckmark = district && confidence !== undefined && confidence < 0.5;

  const handleConfidenceConfirm = () => {
    if (onChangeConfidence) {
      onChangeConfidence(1.0);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <DistrictValueContainer>
        <DistrictContainer
          onClick={saving ? undefined : handleClick}
          $isLowConfidence={showConfidenceCheckmark || false}
          $isSaving={saving || false}
        >
          <Typography variant="body2" style={{ fontWeight: '500', opacity: saving ? 0.6 : 1 }}>
            {displayName}
          </Typography>
        </DistrictContainer>
        {district && onDistrictClear && !saving && (
          <ClearDistrictButton
            aria-label={`Clear ${district.longName || 'district'}`}
            title="Clear district"
            onClick={event => {
              event.stopPropagation();
              onDistrictClear();
            }}
            size="small"
          >
            <Cancel sx={{ fontSize: '14px' }} />
          </ClearDistrictButton>
        )}
      </DistrictValueContainer>
      {canCreateChild && childCreationSuggestion && (
        <IconButton
          aria-label={`Add child district to ${district?.longName || 'district'}`}
          title="Add child district"
          onClick={() => setIsModalOpen(true)}
          size="small"
        >
          <span style={{ display: 'inline-flex', position: 'relative' }}>
            <AccountTree sx={{ fontSize: '18px' }} />
            <Add
              sx={{
                position: 'absolute',
                right: '-5px',
                bottom: '-4px',
                fontSize: '11px',
                borderRadius: '50%',
                backgroundColor: 'white',
              }}
            />
          </span>
        </IconButton>
      )}
      {districtDetailsButton}
      {saving && (
        <CircularProgress size={16} style={{ marginLeft: '4px' }} />
      )}
      {showConfidenceCheckmark && !saving && (
        <IconButton 
          onClick={handleConfidenceConfirm} 
          style={{ marginLeft: '4px' }} 
          size="small"
          title="Confirm this district (set confidence to 100%)"
        >
          <CheckCircle fontSize="small" style={{ color: '#1976d2' }} />
        </IconButton>
      )}
      {isModalOpen && childCreationSuggestion && (
        <CreateDistrictModal
          suggestion={childCreationSuggestion}
          selectableTypes={childTypes}
          stateConfiguration={stateConfiguration}
          suggestedGeometry={suggestedGeometry}
          loadSuggestedGeometry={getSuggestedGeometry}
          creationSources={creationSources}
          creationNotes={creationNotes}
          onCreate={handleCreateDistrict}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

const DistrictSelectInputAny = ({ ...props }: any) => {
  return <DistrictSelectInput {...props} />;
};

interface DistrictContainerProps {
  $isLowConfidence?: boolean;
  $isSaving?: boolean;
}

const DistrictValueContainer = styled.div`
  display: inline-flex;
  position: relative;

  &:hover button,
  &:focus-within button {
    opacity: 1;
    pointer-events: auto;
  }
`;

const ClearDistrictButton = styled(IconButton)`
  position: absolute !important;
  top: -8px;
  right: -8px;
  z-index: 1;
  width: 20px;
  height: 20px;
  padding: 2px !important;
  opacity: 0;
  pointer-events: none;
  background-color: white !important;
  transition: opacity 120ms ease;
`;

const DistrictContainer = styled.div<DistrictContainerProps>`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  background-color: ${props => props.$isLowConfidence ? '#fff3e0' : '#e8f5e8'};
  border: ${props => props.$isLowConfidence ? '1px solid #ffb74d' : 'none'};
  border-radius: 12px;
  max-width: 200px;
  cursor: ${props => props.$isSaving ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.$isSaving ? 0.6 : 1};
  
  &:hover {
    background-color: ${props => props.$isSaving ? (props.$isLowConfidence ? '#fff3e0' : '#e8f5e8') : (props.$isLowConfidence ? '#ffe0b2' : '#c8e6c9')};
  }
`;

const SuggestedDistrictContainer = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  background-color: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 12px;
  max-width: 200px;
  cursor: pointer;
  
  &:hover {
    background-color: #ffeaa7;
  }
`;

const EditingContainer = styled.div`
  display: inline-flex;
  align-items: center;
  max-width: 200px;
`;

export { DistrictCell };

